import { useEffect, useState } from "react";

import { getCachedAvatarUsability, probeAvatarUsability } from "@/lib/images/avatar-probe";

/**
 * Decides whether an avatar URL actually resolves to a photo worth showing.
 *
 * See isUsableAvatarSize (src/lib/images/avatar-image.js) for the why: a placeholder photo loads
 * successfully, so every "did the image load?" signal the platform gives us says yes, and the
 * initials fallback is suppressed in favour of a blank disc. This probes the bitmap once per URL
 * (cached and de-duplicated in avatar-probe.js) and hands callers an empty src for anything that is
 * not a real picture, which puts the fallback back in charge.
 */

/**
 * @param {string | undefined | null} src Avatar URL as stored on the row.
 * @returns {string} the same URL once it is known to be a real photo, otherwise "".
 */
export function useUsableAvatarSrc(src) {
  const url = String(src || "").trim();
  // A URL probed earlier in the session resolves synchronously, so a recycled list row never
  // flashes its initials over an avatar it has already shown.
  const [state, setState] = useState(() => ({ url, usable: getCachedAvatarUsability(url) === true }));
  if (state.url !== url) setState({ url, usable: getCachedAvatarUsability(url) === true });

  useEffect(() => {
    if (!url || getCachedAvatarUsability(url) !== undefined || typeof window === "undefined") return undefined;
    let active = true;
    void probeAvatarUsability(url).then((usable) => {
      if (active && usable) setState({ url, usable: true });
    });
    return () => {
      active = false;
    };
  }, [url]);

  return state.usable ? url : "";
}
